import React, { Fragment, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Helmet from 'react-helmet';

import CardSide from '../CardSide';
import Button from '../../../../components/global/Button';
import { CARD_SIDE } from '../../constants';
import { getAccountCardElementsConfiguration } from './helpers';
import DeliveredByWaldoBadge from '../DeliveredByWaldoBadge';

const propTypes = {
  className: PropTypes.string,
  accountCard: PropTypes.shape({
    id: PropTypes.string.isRequired,
    account_card_sides: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        card_side: PropTypes.shape({
          card_template: PropTypes.object.isRequired,
          card_element_configurations: PropTypes.array.isRequired,
        }).isRequired,
        card_element_configurations: PropTypes.array.isRequired,
      }).isRequired,
    ).isRequired,
  }).isRequired,
  animatable: PropTypes.bool,
  printable: PropTypes.bool,
  onElementConfigurationChange: PropTypes.func.isRequired,
  photosContext: PropTypes.shape({
    account_id: PropTypes.string,
    album_id: PropTypes.string,
    identity_id: PropTypes.string,
    roster_identity_ids: PropTypes.arrayOf(PropTypes.string),
  }),
  defaultSide: PropTypes.oneOf(Object.values(CARD_SIDE)),
  side: PropTypes.oneOf(Object.values(CARD_SIDE)),
  onSideChange: PropTypes.func,
  cardElementLoading: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  viewOnly: PropTypes.bool,
  setSelectedRosterIdentityIds: PropTypes.func,
  onPhotoPpiWarning: PropTypes.func,
  mappings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      path: PropTypes.string,
    }),
  ),
  allowPhotoChange: PropTypes.bool,
  disablePhotoWatermarkOverlay: PropTypes.bool,
  defaultPhoto: PropTypes.shape({
    id: PropTypes.string,
  }),
  onCardElementConfigurationDelete: PropTypes.func,
  onAddAccountCardElementReference: PropTypes.func,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
  linkImageElements: PropTypes.bool,
};

const AccountCard = ({
  className,
  accountCard,
  animatable = true,
  printable,
  viewOnly,
  onElementConfigurationChange,
  photosContext,
  defaultSide = CARD_SIDE.FRONT,
  side,
  onSideChange,
  cardElementLoading,
  setSelectedRosterIdentityIds,
  onPhotoPpiWarning,
  mappings,
  allowPhotoChange = true,
  disablePhotoWatermarkOverlay,
  defaultPhoto,
  onCardElementConfigurationDelete,
  onAddAccountCardElementReference,
  allowOrientationChange,
  watermarkSettings,
  linkImageElements,
}) => {
  const state = getAccountCardElementsConfiguration(
    accountCard,
    cardElementLoading,
    linkImageElements,
  );
  const [stateSide, setStateSide] = useState(defaultSide);
  const flipped = (side ?? stateSide) === CARD_SIDE.BACK;
  const classes = classNames({
    'account-card': true,
    'account-card-animatable': animatable,
    'account-card-printable': printable,
    'account-card-view-only': viewOnly,
    flipped,
    [`account-card-type-${accountCard?.type?.toLowerCase()}`]: !!accountCard?.type,
    [className]: !!className,
  });

  const frontSideElements = useMemo(
    () => state.filter(element => element.side === CARD_SIDE.FRONT) ?? [],
    [accountCard, cardElementLoading],
  );
  const backSideElements = useMemo(
    () => state.filter(element => element.side === CARD_SIDE.BACK) ?? [],
    [accountCard, cardElementLoading],
  );

  return (
    <Fragment>
      <Helmet>
        <link
          href="https://fonts.googleapis.com/css?family=Kalam"
          rel="stylesheet"
        />
      </Helmet>

      <div id={`account-card-${accountCard?.id}`} className={classes}>
        <div className="account-card-wrap">
          {frontSideElements.length > 0 && (
            <CardSide
              cardSide={
                accountCard.account_card_sides.find(
                  accountCardSide => accountCardSide.side === CARD_SIDE.FRONT,
                )?.card_side
              }
              elements={frontSideElements}
              setElementState={onElementConfigurationChange}
              onCardElementConfigurationDelete={
                onCardElementConfigurationDelete
              }
              onAddAccountCardElementReference={
                onAddAccountCardElementReference
              }
              setSelectedRosterIdentityIds={setSelectedRosterIdentityIds}
              photosContext={photosContext}
              accountCard={accountCard}
              viewOnly={viewOnly}
              printable={printable}
              onPhotoPpiWarning={onPhotoPpiWarning}
              mappings={mappings}
              allowPhotoChange={allowPhotoChange}
              disablePhotoWatermarkOverlay={disablePhotoWatermarkOverlay}
              defaultPhoto={defaultPhoto}
              allowOrientationChange={allowOrientationChange}
              watermarkSettings={watermarkSettings}
              linkImageElements={linkImageElements}
            />
          )}

          {backSideElements.length > 0 && (
            <CardSide
              cardSide={
                accountCard.account_card_sides.find(
                  accountCardSide => accountCardSide.side === CARD_SIDE.BACK,
                )?.card_side
              }
              elements={backSideElements}
              setElementState={onElementConfigurationChange}
              setSelectedRosterIdentityIds={setSelectedRosterIdentityIds}
              photosContext={photosContext}
              accountCard={accountCard}
              viewOnly={viewOnly}
              printable={printable}
              onPhotoPpiWarning={onPhotoPpiWarning}
              mappings={mappings}
              allowPhotoChange={allowPhotoChange}
              disablePhotoWatermarkOverlay={disablePhotoWatermarkOverlay}
              defaultPhoto={defaultPhoto}
              onCardElementConfigurationDelete={
                onCardElementConfigurationDelete
              }
              watermarkSettings={watermarkSettings}
              linkImageElements={linkImageElements}
            />
          )}
        </div>
        <DeliveredByWaldoBadge />
      </div>

      {backSideElements.length > 0 && animatable && (
        <div className="text-center account-card-side-edit-toggle">
          <Button
            btnStyle="link"
            btnSize="sm"
            onClick={() => {
              setStateSide(prev => {
                setStateSide(
                  prev === CARD_SIDE.FRONT ? CARD_SIDE.BACK : CARD_SIDE.FRONT,
                );
              });
              onSideChange?.(side ?? stateSide);
            }}
          >
            {flipped ? 'Flip to front' : 'Flip to back'}
          </Button>
        </div>
      )}
    </Fragment>
  );
};

AccountCard.propTypes = propTypes;

export default AccountCard;
